"use client";

import { type LucideIcon, Mail, Phone, TicketCheck } from "lucide-react";
import { useRouter } from "next/navigation";
import { SurfaceCard } from "~/components/dashboard/SurfaceCard";
import { Button } from "~/components/ui/button";
import { Paragraph, Title } from "~/components/ui/Typography";
import {
  SUPPORT_EMAIL,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_TEL,
} from "~/lib/constants/support";
import { cn } from "~/lib/utils";

type ContactOption = {
  icon: LucideIcon;
  label: string;
  description: string;
  action: string;
  onClick: () => void;
};

export function ContactCards({ isAuthed }: { isAuthed: boolean }) {
  const router = useRouter();

  const phoneOption: ContactOption = {
    icon: Phone,
    label: "Call support",
    description: SUPPORT_PHONE_DISPLAY,
    action: "Call now",
    onClick: () => {
      window.location.href = `tel:${SUPPORT_PHONE_TEL}`;
    },
  };

  const emailOption: ContactOption = {
    icon: Mail,
    label: "Email support",
    description: SUPPORT_EMAIL,
    action: "Send email",
    onClick: () => {
      window.location.href = `mailto:${SUPPORT_EMAIL}`;
    },
  };

  // Show an "Open a case" CTA for signed-out visitors so they have a
  // structured-ticket path (redirects through /auth?next=/support/new).
  const options: ContactOption[] = isAuthed
    ? [phoneOption, emailOption]
    : [
        {
          icon: TicketCheck,
          label: "Open a case",
          description:
            "Report a booking or listing issue with our support team",
          action: "Sign in to continue",
          onClick: () => router.push("/auth?next=/support/new"),
        },
        phoneOption,
        emailOption,
      ];

  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-4 sm:grid-cols-2",
        options.length > 2 && "lg:grid-cols-3",
      )}
    >
      {options.map(({ icon: Icon, label, description, action, onClick }) => (
        <SurfaceCard
          key={label}
          className="flex h-full flex-col justify-between"
        >
          <div className="mb-5 flex flex-col items-start gap-4">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-[color:var(--color-surface-3)]">
              <Icon size={20} className="text-[color:var(--color-accent)]" />
            </div>
            <div>
              <Title className="text-[14px] text-[color:var(--color-body)]">
                {label}
              </Title>
              <Paragraph className="mt-1 text-[12px] text-[color:var(--color-secondary)]">
                {description}
              </Paragraph>
            </div>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="w-full"
            onClick={onClick}
          >
            {action}
          </Button>
        </SurfaceCard>
      ))}
    </div>
  );
}
